import { getAuthToken } from './session.js';
import { withRetry, isRetryable } from './utils/retry.js';
import { isDemoMode } from './demo/runtime.js';
import * as demoApi from './demo/demoApi.js';

const config = window.__ANFORDERUNGSAI_CONFIG__ ?? {};
const API_BASE_URL = config.apiBaseUrl?.replace(/\/$/, '') ?? '';
const SESSION_EXPIRED_EVENT = 'anforderungsai:session-expired';

const callApi = (name, realFn) => (...args) => {
  if (isDemoMode()) {
    const demoFn = demoApi[name];
    if (typeof demoFn !== 'function') {
      throw new Error(`Demo API nicht verfügbar: ${name}`);
    }
    return demoFn(...args);
  }
  return realFn(...args);
};

const withBaseUrl = (path) => {
  if (!API_BASE_URL) {
    throw new Error('API Base URL ist nicht konfiguriert (frontend/public/index.html → apiBaseUrl).');
  }
  return `${API_BASE_URL}${path}`;
};

const isObject = (value) => value && typeof value === 'object';

const parseJson = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    console.error('Failed to parse JSON response', text);
    throw error;
  }
};

/**
 * User-friendly error messages by status code
 */
const USER_FRIENDLY_ERRORS = {
  400: 'Die Anfrage enthält ungültige Daten. Bitte überprüfe deine Eingaben.',
  401: 'Bitte melde dich erneut an.',
  403: 'Du hast keine Berechtigung für diese Aktion.',
  404: 'Die angeforderte Ressource wurde nicht gefunden.',
  408: 'Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.',
  409: 'Es existiert bereits ein Eintrag mit diesen Daten.',
  413: 'Die Datei ist zu groß. Maximale Größe: 500 MB.',
  422: 'Die Daten konnten nicht verarbeitet werden. Bitte überprüfe das Format.',
  429: 'Zu viele Anfragen. Bitte warte einen Moment und versuche es erneut.',
  500: 'Ein interner Serverfehler ist aufgetreten. Wir wurden benachrichtigt.',
  502: 'Der Server ist vorübergehend nicht erreichbar. Bitte versuche es in einigen Minuten erneut.',
  503: 'Der Service ist vorübergehend nicht verfügbar. Bitte versuche es später erneut.',
  504: 'Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.',
};

const buildError = async (response) => {
  let payload = null;
  try {
    payload = await parseJson(response);
  } catch {
    // ignore parse errors; payload stays null
  }

  // Use backend message if available, otherwise use friendly message, fallback to generic
  const backendMessage = payload?.message;
  const friendlyMessage = USER_FRIENDLY_ERRORS[response.status];
  const genericMessage = `Request fehlgeschlagen (${response.status})`;

  const message =
    response.status === 401
      ? friendlyMessage || backendMessage || genericMessage
      : backendMessage || friendlyMessage || genericMessage;

  const error = new Error(message);
  error.status = response.status;
  error.userMessage = message;
  error.technicalMessage = backendMessage || `HTTP ${response.status}: ${response.statusText}`;

  // Set error codes for specific status codes
  if (response.status === 401) {
    error.code = 'UNAUTHORIZED';
  } else if (response.status === 403) {
    error.code = 'FORBIDDEN';
  } else if (response.status === 404) {
    error.code = 'NOT_FOUND';
  } else if (response.status === 429) {
    error.code = 'RATE_LIMITED';
  } else if (response.status >= 500) {
    error.code = 'SERVER_ERROR';
  } else if (response.status >= 400) {
    error.code = 'CLIENT_ERROR';
  }

  error.payload = payload;
  return error;
};

const notifySessionExpired = () => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

const handleUnauthorizedError = (error) => {
  if (error?.code === 'UNAUTHORIZED') {
    notifySessionExpired();
  }
};

const assertOk = async (response) => {
  if (response.ok) {
    return response;
  }
  const error = await buildError(response);
  handleUnauthorizedError(error);
  throw error;
};

const authorizedFetch = (path, options = {}) => {
  const headers = { ...(options.headers ?? {}) };
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(withBaseUrl(path), { ...options, headers });
};

const jsonFetch = async (path, { method = 'GET', body, headers = {}, retry, ...rest } = {}) => {
  // Auto-enable retry for GET requests, but allow override
  const shouldRetry = retry !== undefined ? retry : method === 'GET';

  const fetchFn = async () => {
    const finalHeaders = { 'Content-Type': 'application/json', ...headers };
    const response = await authorizedFetch(path, {
      method,
      headers: finalHeaders,
      body: body ? JSON.stringify(body) : undefined,
      ...rest,
    });
    if (!response.ok) {
      const error = await buildError(response);
      handleUnauthorizedError(error);
      throw error;
    }
    if (response.status === 204) {
      return null;
    }
    return parseJson(response);
  };

  if (shouldRetry) {
    return withRetry(fetchFn, { maxRetries: 2 });
  }

  return fetchFn();
};

export const presignUpload = callApi(
  'presignUpload',
  async ({ filename, contentType, customerId, projectId, uploadType, engine, meetingDate }) => {
    const response = await jsonFetch('/api/uploads/presign', {
      method: 'POST',
      body: { filename, contentType, customerId, projectId, uploadType, engine, meetingDate },
    });
    if (!isObject(response)) {
      throw new Error('Unerwartete Antwort vom Server.');
    }
    return response;
  },
);

export const uploadFileToPresignedUrl = callApi('uploadFileToPresignedUrl', async (url, file) => {
  const contentType = typeof file?.type === 'string' && file.type.trim() ? file.type : 'application/octet-stream';
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload fehlgeschlagen (${response.status})`);
  }
});

export const finalizeManualTranscript = callApi('finalizeManualTranscript', async (conversationId) =>
  jsonFetch('/api/manual-transcripts/finalize', {
    method: 'POST',
    body: { conversationId },
  }),
);

export const getConversation = callApi('getConversation', async (conversationId) => {
  const response = await authorizedFetch(`/api/conversations/${conversationId}`);
  await assertOk(response);
  return parseJson(response);
});

export const listConversations = callApi('listConversations', async ({ limit, all } = {}) => {
  const params = new URLSearchParams();
  if (limit) {
    params.set('limit', limit);
  }
  if (all) {
    params.set('all', 'true');
  }
  const search = params.toString() ? `?${params.toString()}` : '';
  const response = await authorizedFetch(`/api/conversations${search}`);
  await assertOk(response);
  const payload = await parseJson(response);
  return Array.isArray(payload?.conversations) ? payload.conversations : [];
});

export const exportRequirements = callApi('exportRequirements', async ({ templateId, requirements }) => {
  if (!templateId) {
    throw new Error('templateId fehlt.');
  }
  return jsonFetch(`/api/requirements/export?templateId=${encodeURIComponent(templateId)}`, {
    method: 'POST',
    body: { requirements },
  });
});

export const listRequirements = callApi('listRequirements', async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.type) params.set('type', filters.type);
  if (filters.subtype) params.set('subtype', filters.subtype);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', filters.limit);
  const search = params.toString() ? `?${params.toString()}` : '';
  const response = await authorizedFetch(`/api/events${search}`);
  if (response.ok) {
    const payload = await parseJson(response);
    const events = Array.isArray(payload?.events) ? payload.events : [];
    const requirements = Array.isArray(payload?.requirements) ? payload.requirements : events;
    return {
      ...payload,
      events,
      requirements,
      count: Number(payload?.count ?? requirements.length),
    };
  }
  if (response.status !== 404) {
    await assertOk(response);
  }
  const fallback = await authorizedFetch(`/api/requirements${search}`);
  await assertOk(fallback);
  const payload = await parseJson(fallback);
  const requirements = Array.isArray(payload?.requirements) ? payload.requirements : [];
  return {
    ...payload,
    events: Array.isArray(payload?.events) ? payload.events : requirements,
    requirements,
    count: Number(payload?.count ?? requirements.length),
  };
});

const normalizeSummaryLanguage = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'en' || normalized === 'fr' || normalized === 'es') {
    return normalized;
  }
  return 'de';
};

const normalizeSummaryRefreshMode = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'force' ? 'force' : 'auto';
};

export const listCustomerProjects = callApi('listCustomerProjects', async () => {
  const payload = await jsonFetch('/api/customer-projects');
  if (!isObject(payload)) {
    return { customers: [] };
  }
  return payload;
});

export const getCustomerProjectSummary = callApi(
  'getCustomerProjectSummary',
  async ({ customerId, projectId, language, refreshMode } = {}) => {
    const normalizedCustomerId = String(customerId ?? '').trim();
    const normalizedProjectId = String(projectId ?? '').trim();

    if (!normalizedCustomerId || !normalizedProjectId) {
      throw new Error('customerId und projectId sind erforderlich.');
    }

    return jsonFetch('/api/customer-projects/summary', {
      method: 'POST',
      body: {
        customerId: normalizedCustomerId,
        projectId: normalizedProjectId,
        language: normalizeSummaryLanguage(language),
        refreshMode: normalizeSummaryRefreshMode(refreshMode),
      },
      retry: false,
    });
  },
);

export const createQaThread = callApi('createQaThread', async ({ context, title } = {}) => {
  const payload = {};
  if (isObject(context)) {
    payload.context = context;
  }
  if (typeof title === 'string' && title.trim()) {
    payload.title = title.trim();
  }
  return jsonFetch('/api/qa/threads', {
    method: 'POST',
    body: payload,
  });
});

export const listQaThreads = callApi('listQaThreads', async ({ limit, contextSignature } = {}) => {
  const params = new URLSearchParams();
  if (Number.isFinite(Number(limit)) && Number(limit) > 0) {
    params.set('limit', String(Math.floor(Number(limit))));
  }
  if (typeof contextSignature === 'string' && contextSignature.trim()) {
    params.set('contextSignature', contextSignature.trim());
  }
  const search = params.toString() ? `?${params.toString()}` : '';
  const response = await authorizedFetch(`/api/qa/threads${search}`);
  await assertOk(response);
  const payload = await parseJson(response);
  return Array.isArray(payload?.threads) ? payload.threads : [];
});

export const getQaThread = callApi('getQaThread', async (threadId, { messageLimit } = {}) => {
  if (!threadId) {
    throw new Error('threadId fehlt.');
  }
  const params = new URLSearchParams();
  if (Number.isFinite(Number(messageLimit)) && Number(messageLimit) > 0) {
    params.set('messageLimit', String(Math.floor(Number(messageLimit))));
  }
  const search = params.toString() ? `?${params.toString()}` : '';
  const response = await authorizedFetch(`/api/qa/threads/${encodeURIComponent(threadId)}${search}`);
  await assertOk(response);
  return parseJson(response);
});

export const sendQaMessage = callApi('sendQaMessage', async (threadId, { message, includeGeneralAnswer, searchScope } = {}) => {
  if (!threadId) {
    throw new Error('threadId fehlt.');
  }
  const payload = { message };
  if (includeGeneralAnswer === true) {
    payload.includeGeneralAnswer = true;
  }
  if (searchScope === 'global' || searchScope === 'context') {
    payload.searchScope = searchScope;
  }
  return jsonFetch(`/api/qa/threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    body: payload,
    retry: false,
  });
});

export const deleteQaThread = callApi('deleteQaThread', async (threadId) => {
  if (!threadId) {
    throw new Error('threadId fehlt.');
  }
  const response = await authorizedFetch(`/api/qa/threads/${encodeURIComponent(threadId)}`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return parseJson(response);
});

export const getResults = callApi('getResults', async (conversationId) => {
  const response = await authorizedFetch(`/api/conversations/${conversationId}/results`);
  await assertOk(response);
  return parseJson(response);
});

export const deleteConversation = callApi('deleteConversation', async (conversationId) => {
  const response = await authorizedFetch(`/api/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return parseJson(response);
});

export const updateConversation = callApi('updateConversation', async (conversationId, updates = {}) =>
  jsonFetch(`/api/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'PATCH',
    body: updates,
    retry: true,
  }),
);

export const updateConversationItem = callApi('updateConversationItem', async (conversationId, payload) =>
  jsonFetch(`/api/conversations/${conversationId}/results`, {
    method: 'PATCH',
    body: payload,
  }),
);

const LEGACY_EXPORT_TYPES = new Set([
  'csv',
  'md',
  'executive-summary',
  'events-json',
  'requirements-json',
  'userstories-json',
  'deferred-decisions-json',
]);

export const getExportUrl = callApi('getExportUrl', async (conversationId, typeOrTemplateId = 'csv') => {
  const normalized = String(typeOrTemplateId ?? '').trim();
  const lower = normalized.toLowerCase();
  const isLegacyType = LEGACY_EXPORT_TYPES.has(lower);
  const isTemplateId = !isLegacyType && normalized.length > 10 && normalized.includes('-');

  const query = isTemplateId
    ? `templateId=${encodeURIComponent(normalized)}`
    : `type=${encodeURIComponent(lower || 'csv')}`;

  const response = await authorizedFetch(`/api/conversations/${conversationId}/export?${query}`);
  await assertOk(response);
  const payload = await parseJson(response);
  const url = payload?.url;
  if (!url) {
    throw new Error('Export-URL nicht verfügbar.');
  }
  return url;
});

export const loginRequest = callApi('loginRequest', (credentials) =>
  jsonFetch('/api/auth/login', {
    method: 'POST',
    body: credentials,
  }),
);

export const registerRequest = callApi('registerRequest', (payload) =>
  jsonFetch('/api/auth/register', {
    method: 'POST',
    body: payload,
  }),
);

export const requestPasswordReset = callApi('requestPasswordReset', (payload) =>
  jsonFetch('/api/auth/forgot-password', {
    method: 'POST',
    body: payload,
  }),
);

export const resetPasswordRequest = callApi('resetPasswordRequest', (payload) =>
  jsonFetch('/api/auth/reset-password', {
    method: 'POST',
    body: payload,
  }),
);

export const currentUserRequest = callApi('currentUserRequest', () =>
  authorizedFetch('/api/auth/me').then(async (response) => {
    await assertOk(response);
    return parseJson(response);
  }),
);

export const logoutRequest = callApi('logoutRequest', () =>
  authorizedFetch('/api/auth/logout', {
    method: 'POST',
  }).then(async (response) => {
    if (!response.ok && response.status !== 401) {
      const error = await buildError(response);
      handleUnauthorizedError(error);
      throw error;
    }
    return null;
  }),
);

export const listUsersRequest = callApi('listUsersRequest', () =>
  authorizedFetch('/api/users').then(async (response) => {
    await assertOk(response);
    return parseJson(response);
  }),
);

export const createUserRequest = callApi('createUserRequest', (payload) =>
  jsonFetch('/api/users', {
    method: 'POST',
    body: payload,
  }),
);

export const updateUserRequest = callApi('updateUserRequest', (email, payload) =>
  jsonFetch(`/api/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: payload,
  }),
);

export const deleteUserRequest = callApi('deleteUserRequest', (email) =>
  authorizedFetch(`/api/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  }).then(async (response) => {
    await assertOk(response);
    return parseJson(response);
  }),
);

export const createCheckoutSession = callApi('createCheckoutSession', (payload) =>
  jsonFetch('/api/billing/checkout/session', {
    method: 'POST',
    body: payload,
  }),
);

export const createSubscription = callApi('createSubscription', (payload) =>
  jsonFetch('/api/billing/create-subscription', {
    method: 'POST',
    body: payload,
    retry: true,
  }),
);

export const createLiveSession = callApi('createLiveSession', (payload = {}) =>
  jsonFetch('/api/live/sessions', {
    method: 'POST',
    body: payload,
  }),
);

export const sendLiveChunk = callApi('sendLiveChunk', (sessionId, payload) =>
  jsonFetch(`/api/live/sessions/${encodeURIComponent(sessionId)}/chunks`, {
    method: 'POST',
    body: payload,
  }),
);

export const stopLiveSession = callApi('stopLiveSession', (sessionId) =>
  jsonFetch(`/api/live/sessions/${encodeURIComponent(sessionId)}/stop`, {
    method: 'POST',
    body: {},
  }),
);

// Export Template Management
export const getExportTemplates = callApi('getExportTemplates', async () => {
  const response = await authorizedFetch('/api/export-templates');
  await assertOk(response);
  return parseJson(response);
});

export const getExportTemplate = callApi('getExportTemplate', async (templateId) => {
  const response = await authorizedFetch(`/api/export-templates/${encodeURIComponent(templateId)}`);
  await assertOk(response);
  return parseJson(response);
});

export const createExportTemplate = callApi('createExportTemplate', async (template) =>
  jsonFetch('/api/export-templates', {
    method: 'POST',
    body: template,
  }),
);

export const updateExportTemplate = callApi('updateExportTemplate', async (templateId, updates) =>
  jsonFetch(`/api/export-templates/${encodeURIComponent(templateId)}`, {
    method: 'PUT',
    body: updates,
    retry: true, // Enable retry for better error tolerance
  }),
);

export const deleteExportTemplate = callApi('deleteExportTemplate', async (templateId) => {
  const response = await authorizedFetch(`/api/export-templates/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return parseJson(response);
});

// Billing Management
export const getBillingInvoices = callApi('getBillingInvoices', async () => {
  const response = await authorizedFetch('/api/billing/invoices');
  await assertOk(response);
  return parseJson(response);
});

export const getBillingPaymentMethod = callApi('getBillingPaymentMethod', async () => {
  const response = await authorizedFetch('/api/billing/payment-method');
  await assertOk(response);
  return parseJson(response);
});

export const createPaymentMethodSetup = callApi('createPaymentMethodSetup', async () =>
  jsonFetch('/api/billing/payment-method', {
    method: 'POST',
    body: {},
    retry: true,
  }),
);

export const updatePaymentMethod = callApi('updatePaymentMethod', async (data) =>
  jsonFetch('/api/billing/payment-method', {
    method: 'PUT',
    body: data,
    retry: true,
  }),
);

export const getBillingHistory = callApi('getBillingHistory', async () => {
  const response = await authorizedFetch('/api/billing/history');
  await assertOk(response);
  return parseJson(response);
});

export const retryBillingStart = callApi('retryBillingStart', async ({ retryToken }) =>
  jsonFetch('/api/billing/retry-start', {
    method: 'POST',
    body: { retryToken },
    retry: false,
  }),
);

export const topupPackageM = callApi('topupPackageM', async ({ topupUnits = 10 } = {}) =>
  jsonFetch('/api/billing/package-m/topup', {
    method: 'POST',
    body: { topupUnits },
    retry: false,
  }),
);

// App Factory API
export const createAppFactorySession = callApi('createAppFactorySession', async (data) =>
  jsonFetch('/api/app-factory/sessions', {
    method: 'POST',
    body: data,
  }),
);

export const listAppFactorySessions = callApi('listAppFactorySessions', async () => {
  const response = await authorizedFetch('/api/app-factory/sessions');
  await assertOk(response);
  return parseJson(response);
});

export const getAppFactorySession = callApi('getAppFactorySession', async (sessionId) => {
  const response = await authorizedFetch(`/api/app-factory/sessions/${sessionId}`);
  await assertOk(response);
  return parseJson(response);
});

export const analyzeAppFactoryProgress = callApi('analyzeAppFactoryProgress', async (sessionId) =>
  jsonFetch(`/api/app-factory/sessions/${sessionId}/analyze-progress`, {
    method: 'POST',
    body: {},
  }),
);

export const updateAppFactoryRequirements = callApi('updateAppFactoryRequirements', async (sessionId, requirements) =>
  jsonFetch(`/api/app-factory/sessions/${sessionId}/requirements`, {
    method: 'PUT',
    body: { requirements },
  }),
);

export const exportAppFactoryYaml = callApi('exportAppFactoryYaml', async (sessionId) => {
  const response = await authorizedFetch(`/api/app-factory/sessions/${sessionId}/export-yaml`);
  await assertOk(response);
  return parseJson(response);
});

export const deleteAppFactorySession = callApi('deleteAppFactorySession', async (sessionId) => {
  const response = await authorizedFetch(`/api/app-factory/sessions/${sessionId}`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return parseJson(response);
});

export const importAppFactoryRequirements = callApi('importAppFactoryRequirements', async (sessionId, conversationId) =>
  jsonFetch(`/api/app-factory/sessions/${sessionId}/import`, {
    method: 'POST',
    body: { conversationId },
  }),
);

const toSalesSearch = (params = {}) => {
  const search = new URLSearchParams();
  if (params.query) {
    search.set('query', params.query);
  }
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};

export const getLatestSalesReport = callApi('getLatestSalesReport', async ({ companyName }) => {
  if (!companyName) {
    throw new Error('companyName fehlt.');
  }
  const params = new URLSearchParams({ companyName });
  return jsonFetch(`/api/sales/reports/latest?${params.toString()}`, { method: 'GET' });
});

export const generateSalesReport = callApi('generateSalesReport', async ({ companyName }) => {
  if (!companyName) {
    throw new Error('companyName fehlt.');
  }
  return jsonFetch('/api/sales/reports/generate', {
    method: 'POST',
    body: { companyName },
  });
});

export const refreshSalesReport = callApi('refreshSalesReport', async (reportId) => {
  if (!reportId) {
    throw new Error('reportId fehlt.');
  }
  return jsonFetch(`/api/sales/reports/${encodeURIComponent(reportId)}/refresh`, {
    method: 'POST',
    body: {},
  });
});

export const getSalesReportVersions = callApi('getSalesReportVersions', async (reportId) => {
  if (!reportId) {
    throw new Error('reportId fehlt.');
  }
  return jsonFetch(`/api/sales/reports/${encodeURIComponent(reportId)}/versions`, { method: 'GET' });
});

export const getSalesReportVersion = callApi('getSalesReportVersion', async (reportId, versionId) => {
  if (!reportId || !versionId) {
    throw new Error('reportId oder versionId fehlt.');
  }
  return jsonFetch(`/api/sales/reports/${encodeURIComponent(reportId)}/versions/${encodeURIComponent(versionId)}`, {
    method: 'GET',
  });
});

export const getSalesReportDiff = callApi('getSalesReportDiff', async ({ reportId, from, to }) => {
  if (!reportId || !from || !to) {
    throw new Error('reportId, from und to sind erforderlich.');
  }
  const params = new URLSearchParams({ from, to });
  return jsonFetch(`/api/sales/reports/${encodeURIComponent(reportId)}/diff?${params.toString()}`, {
    method: 'GET',
  });
});

export const getSalesJob = callApi('getSalesJob', async (jobId) => {
  if (!jobId) {
    throw new Error('jobId fehlt.');
  }
  return jsonFetch(`/api/sales/jobs/${encodeURIComponent(jobId)}`, { method: 'GET' });
});

export const getSalesReportPdfUrl = callApi('getSalesReportPdfUrl', async (reportId, versionId) => {
  if (!reportId || !versionId) {
    throw new Error('reportId oder versionId fehlt.');
  }
  return jsonFetch(`/api/sales/reports/${encodeURIComponent(reportId)}/versions/${encodeURIComponent(versionId)}/pdf`, {
    method: 'GET',
  });
});

export const listSalesCompanies = callApi('listSalesCompanies', async ({ query, limit } = {}) =>
  jsonFetch(`/api/sales/reports/companies${toSalesSearch({ query, limit })}`, { method: 'GET' }),
);

export const discoverSalesCompanies = callApi('discoverSalesCompanies', async ({ query, limit } = {}) =>
  jsonFetch(`/api/sales/reports/discover${toSalesSearch({ query, limit })}`, { method: 'GET' }),
);

export const getReleaseNotes = callApi('getReleaseNotes', async ({ language = 'de', all = false } = {}) => {
  const params = new URLSearchParams();
  if (language) {
    params.set('lang', language);
  }
  if (all) {
    params.set('all', 'true');
  }
  const search = params.toString() ? `?${params.toString()}` : '';
  return jsonFetch(`/api/release-notes/unseen${search}`, { method: 'GET' });
});

export const getUnseenReleaseNotes = callApi('getUnseenReleaseNotes', async (language = 'de') =>
  getReleaseNotes({
    language,
    all: false,
  }),
);

export const ackReleaseNotes = callApi('ackReleaseNotes', async (releaseId) =>
  jsonFetch('/api/release-notes/ack', {
    method: 'POST',
    body: releaseId ? { releaseId } : {},
  }),
);

export const getGeoLanguage = callApi('getGeoLanguage', async () =>
  jsonFetch('/api/geo-language', { method: 'GET' }),
);
